"use client";

// German copy of components/widgetLivePlayground (DE-1). Only the
// component's own fixed strings differ; the embedded editor itself is English.

import React, { useEffect, useRef, useState } from "react";
import { StyledPlayground } from "../widgetLivePlayground/widgetLivePlayground.styles";
import { trackEvent } from '@/lib/track';

// Elements of the full editor we hide while it's embedded on the landing page.
// They come back on the standalone editor because that page isn't touched.
// Targets stable class tokens (semantic names + styled-component display names)
// rather than build-hashed suffixes. Applied by injecting a <style> into the
// iframe document, only possible when same-origin, which is exactly the case
// in production (page + editor are both www.commoninja.com). On localhost the
// iframe is cross-origin, so this safely no-ops.
//
// `.chat-container` is meant for the editor's AI helper only. Two widgets use
// the same class inside their own preview, so those are exempted: the AI
// Chatbot's chat window (it carries the render mode, `editor`, as a class) and
// the message row inside the WhatsApp / Messenger / Telegram chat-button popup.
// Without this the AI Chatbot hub showed only a launcher that never opened.
const EMBED_TRIM_CSS = `
  .chat-container:not(.editor):not(.inner-chat-container .chat-container),
  [class*="StyledGuideModal"],
  .start-guide { display: none !important; }
`;

function trimEmbeddedEditor(iframe: HTMLIFrameElement | null) {
  if (!iframe) return;
  try {
    const doc = iframe.contentDocument;
    if (!doc) return; // cross-origin (e.g. localhost) → can't reach in
    const style = doc.createElement("style");
    style.setAttribute("data-cn-embed-trim", "true");
    style.textContent = EMBED_TRIM_CSS;
    doc.head.appendChild(style);
  } catch (e) {
    /* cross-origin, nothing to do, prod is same-origin */
  }
}

// Counts the visitor committing in the editor's template picker ("Continue with
// this template" or "Start from scratch"). Tracking only: the editor itself then
// opens the full editor in this tab (CommonNinja/apps #2612), so the page never
// resizes or changes. Capture phase, and the click is never blocked, so the
// editor's own handler still runs. Same-origin only, like the trim: on
// localhost the iframe is cross-origin and this safely no-ops.
function trackTemplateCommit(
  iframe: HTMLIFrameElement | null,
  onCommit: (choice: "template" | "scratch") => void
) {
  if (!iframe) return;
  try {
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.addEventListener(
      "click",
      (e) => {
        // Not `instanceof Element`: nodes inside the iframe belong to the
        // iframe's own window, so that check is always false here.
        const el = e.target as Element | null;
        if (!el || typeof el.closest !== "function") return;
        if (el.closest(".continue-with-template")) {
          onCommit("template");
          return;
        }
        const link = el.closest("a, button");
        if (link && link.textContent?.trim() === "Start from scratch") {
          onCommit("scratch");
        }
      },
      true
    );
  } catch (e) {
    /* cross-origin, nothing to do. Prod is same-origin */
  }
}

interface IWidgetLivePlaygroundDeProps {
  // Absolute editor URL (the editor is a separate app, not served by Next).
  // Point this at the Templates view so it opens on the templates tab.
  editorSrc: string;
  // Where the "open full editor" button points (can be a relative app path).
  editorHref: string;
  // Blurred backdrop shown before activation, a screenshot of the EDITOR, so
  // the blur → live-editor transition reads as one continuous surface.
  posterSrc: string;
  // Fixed stage height (desktop) in px. Kept fixed so mounting shifts nothing.
  frameHeight?: number;
  // Pixels of the editor's top toolbar to crop out of view (trim).
  cropTopPx?: number;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  // Label on the pill sitting over the blurred backdrop (before the editor
  // reveals). Defaults to "Try it now"; the countdown page passes a dynamic
  // "Create your <widget>".
  facadeCtaText?: string;
  // When true, the real editor loads and reveals on its own once the section
  // scrolls into view (no click needed). The pill/backdrop become the
  // pre-reveal state. Off by default so other widget pages are unchanged.
  autoOpen?: boolean;
  // How the editor preloads when autoOpen is on:
  //  - "idle" (default): start loading as soon as the browser is idle after
  //    first paint, so the reveal is instant. Costs a background download for
  //    every desktop visitor, even ones who never scroll to it.
  //  - "scroll": don't preload on idle; start loading only when the visitor
  //    scrolls near the section. Slightly slower first open, but no wasted
  //    download for people who never reach it. Use for image-heavy widgets
  //    whose editor pulls megabytes of template thumbnails.
  preloadStrategy?: "idle" | "scroll";
  // When true, the rest of the page dims progressively as this section scrolls
  // toward the centre of the viewport (peaking when the editor is in full view)
  // so the editor stands out, Elfsight-style. Desktop only, off by default.
  dimOnReveal?: boolean;
  // Phone-only CTA label (below breakpointMD the embed is replaced by this button).
  mobileCtaText?: string;
  microcopy?: string;
  eventPrefix?: string;
}

// Live editor playground. Speed model:
//  - Nothing heavy loads on page render, the section is just a blurred image.
//  - On pointer/keyboard INTENT (hover/focus/touch) we quietly start loading the
//    real editor behind the blur, so by the time the visitor clicks "Try it now"
//    it is usually already booted → the reveal feels instant.
//  - Clicking crossfades the blur out to the live editor (semi-seamless).
// The stage has a fixed reserved height (no CLS) and the editor's top chrome is
// cropped for a cleaner in-page look.
export const WidgetLivePlaygroundDe = ({
  editorSrc,
  editorHref,
  posterSrc,
  frameHeight,
  cropTopPx = 0,
  eyebrow = "Probier den Editor aus",
  title = "Gestalte es direkt hier",
  subtitle,
  facadeCtaText = "Jetzt ausprobieren",
  autoOpen = false,
  preloadStrategy = "idle",
  dimOnReveal = false,
  mobileCtaText = "Editor öffnen",
  microcopy = "Keine Kreditkarte nötig · Dauerhaft kostenloser Plan",
  eventPrefix = "widget_playground",
}: IWidgetLivePlaygroundDeProps) => {
  const [warm, setWarm] = useState(false); // editor iframe mounted (loading)
  const [activated, setActivated] = useState(false); // visitor clicked / auto-opened
  const [loaded, setLoaded] = useState(false);
  const [dim, setDim] = useState(0); // page-dim strength (0..1) as it scrolls in
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const warmedRef = useRef(false); // editor load started (via any path), once

  const revealed = activated && loaded;

  const capture = (event: string) => {
    try {
      trackEvent(`${eventPrefix}_${event}`);
    } catch (e) {
      /* never let analytics break the interaction */
    }
    // Also persist to our own DB (prod-only, gated server-side) so playground
    // engagement is readable without PostHog access. Fire-and-forget; keepalive
    // so it survives the tab navigating away on a CTA click.
    try {
      fetch("/api/playground/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event, prefix: eventPrefix }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* never let analytics break the interaction */
    }
  };

  // Start loading the editor iframe behind the blur. Idempotent (warmedRef) so
  // the "warm" event fires once no matter which path triggers it (idle prewarm,
  // scroll, hover).
  const warmUp = () => {
    if (warmedRef.current) return;
    warmedRef.current = true;
    setWarm(true);
    capture("warm");
  };

  const activate = () => {
    warmedRef.current = true;
    setWarm(true);
    setActivated(true);
    capture("activate");
  };

  // Auto-open: when enabled, the editor loads early (in the background) and then
  // reveals itself once the section is scrolled into view, no click needed.
  // Loading and revealing are intentionally decoupled:
  //   1. PREWARM, as soon as the browser is idle after first paint we start the
  //      editor's (slow, cross-origin) boot, well before the visitor reaches it,
  //      so the reveal is usually instant instead of a spinner. Deferred to idle
  //      so it never competes with the host page's own load.
  //   2. REVEAL, an observer crossfades the blur out to the live editor once the
  //      section is comfortably on screen (also warms, as a fallback).
  // Skipped on phones, where the embed isn't rendered at all. Fires once.
  useEffect(() => {
    if (!autoOpen || typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(max-width: 767px)").matches) {
      return;
    }

    const el = rootRef.current;
    const hasIO = typeof IntersectionObserver !== "undefined";

    // 1. Prewarm.
    //  - "idle": start on browser idle, well before the visitor reaches it.
    //  - "scroll": don't preload on idle; warm only when the section gets near
    //    the viewport, so nothing downloads for visitors who never scroll to it.
    let idleId: number | null = null;
    let approachObs: IntersectionObserver | null = null;
    const hasRIC = typeof window.requestIdleCallback === "function";
    if (preloadStrategy === "scroll") {
      approachObs =
        el && hasIO
          ? new IntersectionObserver(
              (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                  warmUp();
                  approachObs?.disconnect();
                }
              },
              // No positive margin: the section sits just ~70px below the fold,
              // so any early margin would fire at page load and defeat the whole
              // point (staying cold until the visitor scrolls to it). This warms
              // the moment the section's top edge enters view, still a head start
              // over the reveal trigger below, which needs the section 30% in.
              { root: null, rootMargin: "0px", threshold: 0 }
            )
          : null;
      if (approachObs && el) approachObs.observe(el);
    } else {
      idleId = hasRIC
        ? window.requestIdleCallback(() => warmUp(), { timeout: 2500 })
        : window.setTimeout(() => warmUp(), 1500);
    }

    // 2. Reveal when the section is comfortably in view (also warms, as a
    //    fallback, if prewarm somehow hasn't run yet).
    let opened = false;
    const openObs =
      el && hasIO
        ? new IntersectionObserver(
            (entries) => {
              if (!opened && entries.some((e) => e.isIntersecting)) {
                opened = true;
                warmUp();
                setActivated(true);
                capture("auto_activate");
                openObs?.disconnect();
              }
            },
            { root: null, rootMargin: "0px 0px -30% 0px", threshold: 0 }
          )
        : null;
    if (openObs && el) openObs.observe(el);

    return () => {
      if (idleId !== null) {
        if (hasRIC && typeof window.cancelIdleCallback === "function") {
          window.cancelIdleCallback(idleId);
        } else {
          clearTimeout(idleId);
        }
      }
      approachObs?.disconnect();
      openObs?.disconnect();
    };
    // warmUp/capture/setState are stable; run once when autoOpen turns on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen, preloadStrategy]);

  // Elfsight-style dim: darken the rest of the page as this section scrolls
  // toward the centre of the viewport, peaking when the editor is in full view,
  // so the editor pops. Desktop only. rAF-throttled scroll listener maps
  // how-centred → dim strength.
  useEffect(() => {
    if (!dimOnReveal || typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(max-width: 767px)").matches) {
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const center = rect.top + rect.height / 2;
      const dist = Math.abs(center - vh / 2);
      const maxDist = vh / 2 + rect.height / 2;
      const progress = 1 - Math.min(1, dist / maxDist);
      // Ease in (progress²) so it stays subtle at the edges and deepens as the
      // editor centres; cap so the page dims but never goes fully black.
      setDim(Math.min(0.72, progress * progress * 0.85));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimOnReveal]);

  const onTemplateCommit = (choice: "template" | "scratch") => {
    capture(choice === "template" ? "template_continue" : "scratch_continue");
  };

  const stageStyle: React.CSSProperties = {};
  if (frameHeight) stageStyle.height = frameHeight;
  if (cropTopPx) {
    (stageStyle as Record<string, string>)["--crop"] = `${cropTopPx}px`;
  }

  return (
    <StyledPlayground
      ref={rootRef}
      // Once the page dims, the heading + microcopy float above the dark scrim,
      // so switch their text to light colors to stay readable.
      className={dim > 0.12 ? "is-dimmed" : undefined}
      aria-label="Live-Editor zum Ausprobieren"
      // Observed by WidgetNavbar: the floating "Try for free" CTA stays hidden
      // until the visitor has scrolled completely past the playground.
      data-cn-live-playground
    >
      {dimOnReveal && (
        <span className="pg-dim" style={{ opacity: dim }} aria-hidden="true" />
      )}
      <div className="pg-inner">
        <div className="pg-head">
          <p className="pg-eyebrow">{eyebrow}</p>
          <h2 className="pg-title">{title}</h2>
          {subtitle && <p className="pg-sub">{subtitle}</p>}
        </div>

        <div className="pg-frame">
          <div className="pg-bar">
            <span />
            <span />
            <span />
          </div>

          <div
            className={`pg-stage ${cropTopPx && warm ? "is-cropped" : ""}`}
            style={stageStyle}
          >
            {warm && (
              <iframe
                ref={iframeRef}
                className={`pg-editor ${revealed ? "is-shown" : ""}`}
                src={editorSrc}
                title="Live-Editor von Common Ninja (auf Englisch)"
                loading="lazy"
                allow="clipboard-read; clipboard-write; fullscreen"
                onLoad={() => {
                  setLoaded(true);
                  trimEmbeddedEditor(iframeRef.current);
                  trackTemplateCommit(iframeRef.current, onTemplateCommit);
                }}
              />
            )}

            <button
              type="button"
              className={`pg-facade ${revealed ? "is-gone" : ""}`}
              onMouseEnter={warmUp}
              onFocus={warmUp}
              onTouchStart={warmUp}
              onClick={activate}
              aria-label="Editor jetzt ausprobieren"
              aria-hidden={revealed}
            >
              <span
                className="pg-blur"
                style={{ backgroundImage: `url(${posterSrc})` }}
                aria-hidden="true"
              />
              <span className="pg-unlock">
                <span className="pg-try">{facadeCtaText}</span>
                <span className="pg-try-sub">
                  {activated ? "Editor wird geöffnet…" : "Kostenlos · ohne Registrierung · live bearbeiten"}
                </span>
              </span>
            </button>
          </div>
        </div>

        {/* Phones: the full editor embed is a poor fit on a small screen, so
            below breakpointMD we hide it (and its poster) and show a CTA. */}
        <div className="pg-mobile">
          <a
            className="pg-cta"
            href={editorHref}
            onClick={() => capture("mobile_cta_click")}
          >
            {mobileCtaText}
            <span aria-hidden="true">→</span>
          </a>
          {microcopy && <p className="pg-microcopy">{microcopy}</p>}
        </div>

        {microcopy && (
          <div className="pg-cta-row">
            <p className="pg-microcopy">{microcopy}</p>
          </div>
        )}
      </div>
    </StyledPlayground>
  );
};
